import { useCallback, useEffect, useMemo, useState } from 'react';
import { Nav, Button, Input, InputGroup, Loader, Panel, Dropdown, IconButton, Modal } from 'rsuite';
import {
  FaWhatsapp,
  FaPlus,
  FaSync,
  FaSearch,
  FaEllipsisV,
  FaRegFileAlt,
  FaHeading,
  FaCode,
  FaRegDotCircle,
  FaLock,
  FaRegLightbulb,
  FaEyeSlash,
  FaUndo,
  FaCog,
  FaCoins,
  FaRobot,
} from 'react-icons/fa';
import { useAuth } from '../../../hooks/auth';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { toastSuccess, toastError } from '../../../toasts';
import { CustomToggle } from '../../../components/CustomToggle';
import {
  listTemplates,
  syncTemplates,
  deleteTemplate,
  createDefaultTemplate,
  restoreTemplate,
  getConnection,
  saveConnection,
} from '../../../services/uniservice/uniservice.service';
import CreateEditTemplateModal from './CreateEditTemplateModal';
import TemplateBubble from './TemplateBubble';
import {
  STATUS,
  CATEGORY_LABEL,
  FILTERS,
  EDITABLE_STATUS,
  numberedVars,
  PROTECTED_TEMPLATE_NAME,
} from './constants';
import { styles } from './styles';

export default function Templates() {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [creatingDefault, setCreatingDefault] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState({ open: false, template: null });
  const [toDelete, setToDelete] = useState(null);
  const [connection, setConnection] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // includeHidden: traz também os soft-deleted (aba "Ocultos").
    const [tpls, conn] = await Promise.all([
      listTemplates(id_empresa, 'all', true),
      getConnection(id_empresa),
    ]);
    setTemplates(tpls);
    setConnection(conn);
    setLoading(false);
  }, [id_empresa]);

  useEffect(() => {
    load();
  }, [load]);

  const hasDefault = useMemo(
    () => templates.some((t) => t.name === PROTECTED_TEMPLATE_NAME && !t.hidden),
    [templates],
  );

  const counts = useMemo(() => {
    const by = { ALL: 0, APPROVED: 0, PENDING: 0, REJECTED: 0, HIDDEN: 0 };
    templates.forEach((t) => {
      if (t.hidden) {
        by.HIDDEN += 1;
        return;
      }
      by.ALL += 1;
      if (t.status === 'APPROVED') by.APPROVED += 1;
      else if (t.status === 'REJECTED') by.REJECTED += 1;
      else by.PENDING += 1;
    });
    return by;
  }, [templates]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return templates.filter((t) => {
      // Ocultos só aparecem na aba "Ocultos"; as demais abas os escondem.
      if (filter === 'HIDDEN') {
        if (!t.hidden) return false;
      } else if (t.hidden) {
        return false;
      }
      const matchesFilter =
        filter === 'ALL' ||
        filter === 'HIDDEN' ||
        (filter === 'APPROVED' && t.status === 'APPROVED') ||
        (filter === 'REJECTED' && t.status === 'REJECTED') ||
        (filter === 'PENDING' && !['APPROVED', 'REJECTED'].includes(t.status));
      const matchesSearch =
        !term ||
        (t.name || '').toLowerCase().includes(term) ||
        (t.bodyText || '').toLowerCase().includes(term);
      return matchesFilter && matchesSearch;
    });
  }, [templates, filter, search]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const data = await syncTemplates(id_empresa);
      const result = data?.result || data || {};
      await load();
      toastSuccess(
        `Sincronizado: ${result.created || 0} novos, ${result.updated || 0} atualizados, ${result.deleted || 0} removidos.`,
      );
    } catch {
      toastError('Não foi possível sincronizar com a Meta');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateDefault = async () => {
    setCreatingDefault(true);
    try {
      const res = await createDefaultTemplate(id_empresa);
      if (res?.ok) toastSuccess(res.message || 'Template padrão criado!');
      else toastError(res?.message || 'Não foi possível criar o template padrão.');
      await load();
    } catch {
      toastError('Não foi possível criar o template padrão.');
    } finally {
      setCreatingDefault(false);
    }
  };

  const handleRestore = async (template) => {
    try {
      await restoreTemplate(id_empresa, template.id);
      toastSuccess('Template restaurado');
      load();
    } catch {
      toastError('Não foi possível restaurar o template');
    }
  };

  const toggleActive = async (v) => {
    setSavingConfig(true);
    try {
      setConnection(await saveConnection(id_empresa, { active: v }));
      toastSuccess(v ? 'Atendimento ativado' : 'Atendimento pausado');
    } catch {
      toastError('Não foi possível salvar a configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleSendOrderTemplate = async (v) => {
    setSavingConfig(true);
    try {
      setConnection(await saveConnection(id_empresa, { sendOrderTemplate: v }));
    } catch {
      toastError('Não foi possível salvar a configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleSendOrderMirror = async (v) => {
    setSavingConfig(true);
    try {
      setConnection(await saveConnection(id_empresa, { sendOrderMirror: v }));
    } catch {
      toastError('Não foi possível salvar a configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const confirmDelete = async () => {
    // Guarda extra: o template essencial nunca é excluível (o backend também bloqueia).
    if (!toDelete || toDelete.name === PROTECTED_TEMPLATE_NAME) {
      setToDelete(null);
      return;
    }
    try {
      const res = await deleteTemplate(id_empresa, toDelete.id);
      toastSuccess(
        res?.hidden
          ? 'Template removido da sua lista (a Meta não permite excluí-lo de fato).'
          : 'Template excluído',
      );
      setToDelete(null);
      load();
    } catch {
      toastError('Não foi possível excluir o template');
    }
  };

  return (
    <div style={styles.page(theme)}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title(theme)}>Templates oficiais</h2>
          <p style={styles.subtitle(theme)}>
            Mensagens aprovadas pela Meta usadas para reengajar o cliente fora da janela de 24h.
          </p>
        </div>
        <div style={styles.toolbar}>
          <Button appearance="ghost" startIcon={<FaCog />} onClick={() => setConfigOpen(true)}>
            Configurações
          </Button>
          <Button appearance="ghost" startIcon={syncing ? <Loader /> : <FaSync />} onClick={handleSync} disabled={syncing}>
            Sincronizar
          </Button>
          <Button
            appearance="primary"
            startIcon={<FaPlus />}
            onClick={() => setEditor({ open: true, template: null })}
            style={{ backgroundColor: theme.colors.primary }}
          >
            Novo template
          </Button>
        </div>
      </div>

      {!loading && !hasDefault && (
        <Panel bordered style={styles.defaultCallout(theme)}>
          <div style={styles.calloutInner}>
            <div style={styles.calloutIcon(theme)}>
              <FaRegLightbulb size={20} />
            </div>
            <div style={styles.calloutBody}>
              <div style={styles.calloutTitle(theme)}>
                Ative as notificações de pedido no WhatsApp
              </div>
              <div style={styles.calloutText(theme)}>
                Para usar a API oficial do WhatsApp no MenuCheff, você precisa criar o template
                padrão do nosso sistema. É ele que avisa o cliente sobre o pedido mesmo fora da
                janela de 24h.
              </div>
            </div>
            <Button
              appearance="primary"
              loading={creatingDefault}
              onClick={handleCreateDefault}
              style={{ backgroundColor: theme.colors.primary, whiteSpace: 'nowrap' }}
            >
              Criar template padrão
            </Button>
          </div>
        </Panel>
      )}

      <div style={styles.controls}>
        <Nav appearance="subtle" activeKey={filter} onSelect={setFilter}>
          {FILTERS.map((f) => (
            <Nav.Item key={f.key} eventKey={f.key}>
              {f.label} ({counts[f.key] ?? 0})
            </Nav.Item>
          ))}
        </Nav>
        <InputGroup inside style={{ maxWidth: 260 }}>
          <Input placeholder="Buscar template..." value={search} onChange={setSearch} />
          <InputGroup.Addon><FaSearch /></InputGroup.Addon>
        </InputGroup>
      </div>

      {loading ? (
        <Loader center size="md" content="Carregando templates..." />
      ) : visible.length === 0 ? (
        <Panel bordered style={styles.emptyCard(theme)}>
          <div style={styles.emptyState}>
            <FaRegFileAlt size={34} color={theme.colors.primary} />
            <p style={styles.emptyText(theme)}>
              {templates.length === 0
                ? 'Nenhum template cadastrado. Crie o primeiro ou sincronize com a Meta.'
                : 'Nenhum template encontrado para este filtro.'}
            </p>
          </div>
        </Panel>
      ) : (
        <div style={styles.grid}>
          {visible.map((template) => (
            <TemplateCard
              key={template.id || template.name}
              template={template}
              theme={theme}
              onEdit={() => setEditor({ open: true, template })}
              onDelete={() => setToDelete(template)}
              onRestore={() => handleRestore(template)}
            />
          ))}
        </div>
      )}

      <CreateEditTemplateModal
        open={editor.open}
        template={editor.template}
        companyId={id_empresa}
        onClose={() => setEditor({ open: false, template: null })}
        onSaved={load}
      />

      <Modal open={configOpen} onClose={() => setConfigOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Configurações de notificação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={styles.configIntro(theme)}>
            Como o MenuCheff atende e avisa o cliente sobre o pedido pelo WhatsApp oficial.
          </p>

          <ConfigOption
            theme={theme}
            icon={<FaRobot size={16} />}
            title="Atendimento automático"
            description="Quando pausado, o robô não responde nem envia status de pedido."
            checked={Boolean(connection?.active)}
            onChange={toggleActive}
            disabled={savingConfig}
          />

          <ConfigOption
            theme={theme}
            icon={<FaRegFileAlt size={16} />}
            title="Template automático"
            description="Envia o template aprovado pela Meta com os detalhes do pedido para avisar o cliente fora da janela de 24h."
            checked={Boolean(connection?.sendOrderTemplate)}
            onChange={toggleSendOrderTemplate}
            disabled={savingConfig}
          />

          <ConfigOption
            theme={theme}
            icon={<FaWhatsapp size={16} />}
            title="Espelho do pedido"
            description="Ao finalizar a compra, redireciona o cliente pro WhatsApp da loja com um resumo do pedido pronto pra enviar. Confirma o número dele e libera as notificações seguintes sem gastar template."
            checked={Boolean(connection?.sendOrderMirror)}
            onChange={toggleSendOrderMirror}
            disabled={savingConfig}
          />

          {connection?.sendOrderTemplate && !connection?.sendOrderMirror && (
            <div style={styles.costHint(theme)}>
              <div style={styles.costHintIcon(theme)}>
                <FaCoins size={13} />
              </div>
              <div>
                <strong>Economize:</strong> a Meta cobra mais caro por template do que por conversa aberta pelo
                cliente. Ative o espelho também — na maioria dos casos ele reduz seus custos de notificação.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={() => setConfigOpen(false)} style={{ backgroundColor: theme.colors.primary }}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={Boolean(toDelete)} onClose={() => setToDelete(null)} size="xs">
        <Modal.Header>
          <Modal.Title>Excluir template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Excluir <strong>{toDelete?.name}</strong>? Isso remove o template na Meta também.
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={() => setToDelete(null)}>Cancelar</Button>
          <Button appearance="primary" color="red" onClick={confirmDelete}>Excluir</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function ConfigOption({ theme, icon, title, description, checked, onChange, disabled }) {
  return (
    <div style={styles.configCard(theme, checked)}>
      <div style={styles.configIcon(theme, checked)}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.configLabel(theme)}>{title}</div>
        <div style={styles.configDesc(theme)}>{description}</div>
      </div>
      <CustomToggle checked={checked} onChange={onChange} disabled={disabled} checkedChildren="Ativo" unCheckedChildren="Inativo" />
    </div>
  );
}

function TemplateCard({ template, theme, onEdit, onDelete, onRestore }) {
  const status = STATUS[template.status] || { label: template.status || '—', color: 'blue' };
  const vars = numberedVars(template.bodyText);
  const buttons = Array.isArray(template.buttons) ? template.buttons : [];
  const canEdit = EDITABLE_STATUS.includes(template.status);
  const isProtected = template.name === PROTECTED_TEMPLATE_NAME;
  const isHidden = Boolean(template.hidden);
  const isPending = ['PENDING', 'IN_APPEAL'].includes(template.status) && !isHidden;
  const getExample = (n) => (template.exampleValues || [])[vars.indexOf(n)];
  const showQuality = template.qualityScore && template.qualityScore !== 'UNKNOWN';

  return (
    <Panel bordered style={styles.templateCard(theme, isProtected, isHidden)} bodyFill>
      <div style={styles.templateHead}>
        <div style={styles.templateIcon(theme)}>
          <FaWhatsapp size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.templateNameRow}>
            <span style={styles.templateName(theme)}>{template.name}</span>
            {isProtected && (
              <span style={styles.protectedBadge(theme)}>
                <FaLock size={9} /> Padrão
              </span>
            )}
            {isHidden && (
              <span style={styles.hiddenBadge(theme)}>
                <FaEyeSlash size={9} /> Oculto
              </span>
            )}
          </div>
          <div style={styles.templateMeta(theme)}>
            {CATEGORY_LABEL[template.category] || template.category || '—'} · {template.language || 'pt_BR'}
          </div>
        </div>
        <Dropdown
          placement="bottomEnd"
          renderToggle={(props) => <IconButton {...props} appearance="subtle" size="sm" icon={<FaEllipsisV />} />}
        >
          {isHidden ? (
            <Dropdown.Item onSelect={onRestore} icon={<FaUndo />}>Restaurar</Dropdown.Item>
          ) : (
            <>
              <Dropdown.Item disabled={!canEdit} onSelect={onEdit}>Editar</Dropdown.Item>
              {isProtected ? (
                <Dropdown.Item disabled icon={<FaLock />}>Protegido</Dropdown.Item>
              ) : (
                <Dropdown.Item onSelect={onDelete} style={{ color: '#f5222d' }}>Excluir</Dropdown.Item>
              )}
            </>
          )}
        </Dropdown>
      </div>

      <div style={styles.statusRow}>
        <span style={styles.statusDot(status.color)} />
        <span style={styles.statusLabel(theme)}>{status.label}</span>
        {showQuality && <span style={styles.qualityChip(theme)}>Qualidade {template.qualityScore}</span>}
      </div>

      <div style={{ padding: '0 12px' }}>
        <TemplateBubble
          headerType={template.headerType}
          headerContent={template.headerContent}
          bodyText={template.bodyText}
          footerText={template.footerText}
          buttons={buttons}
          getExample={getExample}
        />
      </div>

      <div style={styles.metaChips}>
        {template.headerType && template.headerType !== 'NONE' && (
          <span style={styles.metaChip(theme)}><FaHeading size={10} /> Cabeçalho: {template.headerType}</span>
        )}
        {vars.length > 0 && <span style={styles.metaChip(theme)}><FaCode size={10} /> {vars.length} variáveis</span>}
        {buttons.length > 0 && <span style={styles.metaChip(theme)}><FaRegDotCircle size={10} /> {buttons.length} botões</span>}
      </div>

      {isPending && (
        <div style={styles.pendingNotice(theme, isProtected)}>
          ⏳ Aguardando aprovação da Meta — costuma levar de alguns minutos a algumas
          horas (em geral até 24h).
          {isProtected
            ? ' As notificações de pedido começam a funcionar assim que ele for aprovado.'
            : ' Ele poderá ser enviado após a aprovação.'}
        </div>
      )}

      {template.status === 'REJECTED' && template.rejectedReason && (
        <div style={styles.rejectReason}>Motivo: {template.rejectedReason}</div>
      )}
    </Panel>
  );
}
